import { CartQuery } from "lib/api";
import { useRouter } from "next/router";
import type { FC } from "react";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useCartStore } from "store/cart";
import { tw } from "twind";
import { ActionBar } from "./ActionBar";
import Testimonial from "@/components/NewCart/Testimonial";
import useQueryString from "../../hooks/useQueryString";
import { useRudderstack } from "analytics/RudderStack";
import CheckoutLoading from "@/components/Animation/CheckoutLoading";
import { BrandLogo } from "@/components/NewCart/BrandLogo";
import { ListItem } from "@/components/ProductList/ListItem";
import { DiscountModal } from "@/components/DiscountModal";
import useDiscountPopupTimeout from "../../hooks/useDiscountPopupTimeout";
import { PromoMessage } from "@/components/NewCart/PromoMessage";

interface ProductListCartProps {
  cart: CartQuery['Cart'];
  referer: string;
}

const Avatar: FC<{
  src: string;
  className?: string;
}> = (props) => (
  <a href="">
    <img className={tw`inline-block h-14 w-14 rounded-full border border-black ${props.className}`} src={props.src} alt="avatar" />
  </a>
);

export const CreatorHandle: FC<{
  handle: string;
  full_url: string;
}> = ({ handle, full_url }) => {
  const { theme } = useCartStore();

  return (
    <a href={full_url} className={tw`font-bold text-${theme}-primary`} rel="nofollow noreferrer" target="_blank">
      <span>@{handle}</span>
    </a>
  );
};

export const ProductListCart: FC<ProductListCartProps> = ({ cart, referer }) => {
  const router = useRouter();
  const [queryParams,] = useQueryString();
  const { theme, loaded, setCart, applyDiscounts, discountsApplied } = useCartStore();
  const [showCheckoutLoading, setShowCheckoutLoading] = React.useState(false);
  const [discountModal, setDiscountModal] = useState(false);
  const [usesDiscountModal, setUsesDiscountModal] = useState(false);
  const rudder = useRudderstack();

  useEffect(() => {
    // initialize our store (once)
    if (loaded) {
      return;
    }
    setCart(cart);
  }, [loaded, setCart, cart]);

  useEffect(() => {
    if (queryParams?.origin === 'sms') {
      applyDiscounts();
    }
  }, [queryParams?.origin]);

  useDiscountPopupTimeout(cart, String(queryParams?.origin), setDiscountModal, setUsesDiscountModal);

  useLayoutEffect(() => {
    rudder.track('cart-visit', {
      cart_id: cart.id,
      step: 1,
    });
  }, [rudder, cart.id]);

  const getCheckoutUrl = useMemo(() => {
    if (cart.discount_code && discountsApplied) {
      return (productUrl: string) => `${productUrl}?discount_code=${cart.discount_code}`;
    }

    return (url: string) => url;
  }, [discountsApplied, cart.discount_code]);

  const proceedToProduct = useCallback((productUrl: string) => {
    if (productUrl === '') {
      return;
    }

    setShowCheckoutLoading(true);
    router.push(getCheckoutUrl(productUrl));
    rudder.track('checkout-visit', { cart_id: cart.id, step: 3 });
  }, [cart.id, getCheckoutUrl, router, rudder]);

  const applyDiscountsCallback = useCallback(() => {
    setDiscountModal(false);
    applyDiscounts();
  }, []);

  const onPromoMessageClick = useCallback(() => {
    if (!cart.popup_discounts) {
      return;
    }

    setDiscountModal(true);
  }, [cart, setDiscountModal]);

  const creatorHandle = cart.creator.socials?.[0]?.handle;

  return (
    <>
      <form className={tw`border-t-[6px] border-${theme} theme-${theme}`}>
        <div className={tw`sm:px-[24px]`}>
          {cart.shop?.avatar && <BrandLogo src={cart.shop?.avatar} alt={cart.shop?.display_name} />}

          <div className={tw`px-[6px] pb-24`}>
            <div className={tw`flex flex-col align-middle items-center pb-4`}>
              <Avatar
                src={cart.creator.avatar}
                className={tw`rounded-full my-[12px] border-black border-2`}
              />
              <CreatorHandle handle={creatorHandle} full_url={cart.creator.socials?.[0]?.full_url} />
              {cart.testimonial && <Testimonial quote={cart.testimonial} />}
            </div>

            {loaded && (
              <div className={tw`flex flex-col items-center`}>

                <PromoMessage onPromoMessageClick={ onPromoMessageClick } />

                {cart.items.map((item) => <ListItem key={item.id} item={item} onAddToCart={proceedToProduct} applyDiscounts={discountsApplied} usesDiscountModal={usesDiscountModal} />)}
              </div>
            )}
          </div>
        </div>

        <ActionBar />

        <DiscountModal
          isOpen={discountModal}
          requestClose={() => setDiscountModal(false)}
          onSuccess={applyDiscountsCallback}
          cartId={cart.id}
          creatorId={cart.creator.id}
          message={cart.discount_message_template ?? 'Your phone number'}
        />
      </form>

      {showCheckoutLoading && <CheckoutLoading theme={theme} brandName={cart.shop?.display_name} />}
    </>
  );
};
